import { useState } from 'react';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import ToggleSwitch from '@/components/ToggleSwitch';
import { ProductList, ProductListUpdate } from '@/models/ProductList';
import { Status } from '@/models/Status';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';

interface ProductListFormProps {
  onSubmit: (productList: ProductListUpdate) => Promise<void> | void;
  productList?: ProductList;
  errorMessage?: string | null;
}

interface ProductListFormValues {
  name: string;
  description: string;
  status: Status;
}

const ProductListForm = ({ onSubmit, productList, errorMessage }: ProductListFormProps) => {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ProductListFormValues>({
    defaultValues: productList
      ? {
          name: productList.name,
          description: productList.description,
          status: productList.status,
        }
      : {
          name: '',
          description: '',
          status: 'ACTIVE',
        },
  });

  const status = watch('status');

  const handleStatusChange = (checked: boolean) => {
    setValue('status', checked ? 'ACTIVE' : 'INACTIVE');
  };

  const onSubmitForm = async (data: ProductListFormValues) => {
    setLoading(true);
    try {
      await onSubmit({
        name: data.name,
        description: data.description,
        externalCode: productList?.externalCode || undefined,
        status: data.status,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
      {errorMessage && <Alert type="error" message={errorMessage} />}

      <div className="grid grid-cols-2 gap-4 lg:gap-8">
        <div className="col-span-2">
          <label htmlFor="name" className="block font-medium text-gray-700">
            Name:
          </label>
          <input
            type="text"
            id="name"
            {...register('name', { required: 'Name is required' })}
            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
          />
          {errors.name && <span className="text-red-500">{errors.name.message}</span>}
        </div>

        <div className="col-span-2">
          <label htmlFor="description" className="block font-medium text-gray-700">
            Description:
          </label>
          <textarea
            id="description"
            {...register('description', { required: 'Description is required' })}
            className="mt-1 p-2 border border-gray-300 rounded-md w-full"
          />
          {errors.description && <span className="text-red-500">{errors.description.message}</span>}
        </div>

        <div className="col-span-2">
          <ToggleSwitch
            checked={status === 'ACTIVE'}
            onChange={handleStatusChange}
            labelText="Status:"
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          type="submit"
          icon={<CheckIcon className="h-6 w-6" aria-hidden="true" />}
          loadingIconClassName="h-5 w-5 mr-1"
          variant="primary"
          loading={loading}
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default ProductListForm;
